import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxes, FaBoxOpen, FaCamera, FaCartPlus, FaCog, FaEdit, FaFileDownload, FaFileUpload, FaImage, FaList, FaPlus, FaQrcode, FaSearch, FaTag, FaTh, FaTimes, FaTrash } from 'react-icons/fa';
import { Badge, Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { productActions } from '../../sagas/productSlice';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import config from '../../helpers/config';
import { PRODUCTS_FORM_PAGE } from '../../routes/routeConstants';
import { useNavigate } from 'react-router-dom';
import { categoryActions } from '../../sagas/categorySlice';
import { formatPrice } from '../../helpers/utility';
import { unitesActions } from '../../sagas/unitesSlice';

const ProductList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const [displayMode, setDisplayMode] = useState('card'); // or 'card'
  const [currentPage, setCurrentPage] = useState(1);
  const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unites
  const role = useSelector((state) => state.auth.role);

  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const {
    products,
    loading,
    error,
    totalElements,
    totalPages,
    pageNumber,
    pageSize
  } = useSelector((state) => state.products);
  const navigate = useNavigate();

  useEffect(() => {
    // Auto focus on mount
    searchInputRef.current?.focus();

    // Add keyboard shortcut
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [products]);

  useEffect(() => {
    dispatch(productActions.findAllUnites());
    dispatch(categoryActions.list()); // Fetch categories on component mount
  }, []);


  useEffect(() => {
    dispatch(productActions.findAll({
      search: searchTerm,
      page: currentPage - 1,
      size: pageSize,
    }));

  }, [currentPage]);

  // Color options
  const couleurOptions = [
    { value: 'ROUGE', label: 'Rouge', color: '#FF0000' },
    { value: 'VERT', label: 'Vert', color: '#00FF00' },
    { value: 'BLEU', label: 'Bleu', color: '#0000FF' },
    { value: 'JAUNE', label: 'Jaune', color: '#FFFF00' },
    { value: 'ORANGE', label: 'Orange', color: '#FFA500' },
    { value: 'VIOLET', label: 'Violet', color: '#8A2BE2' },
    { value: 'NOIR', label: 'Noir', color: '#000000' },
    { value: 'BLANC', label: 'Blanc', color: '#FFFFFF' },
    { value: 'GRIS', label: 'Gris', color: '#808080' },
    { value: 'MARRON', label: 'Marron', color: '#A52A2A' },
    { value: 'CYAN', label: 'Cyan', color: '#00FFFF' },
    { value: 'MAGENTA', label: 'Magenta', color: '#FF00FF' }
  ];

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      debouncedSearch(searchTerm); // Trigger search when Enter is pressed
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value); // Use debounced function directly to ensure it triggers only once
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setIsSearching(true);
      dispatch(productActions.findAll({
        search: query,
        page: 0,
        size: pageSize,
      }));
      setIsSearching(false);
    }, 1000), // Wait 1 second after user stops typing
    []
  );



  const refrechProducts = () => {
    setIsSearching(true);
    dispatch(productActions.findAll({
      search: searchTerm,
      page: 0,
      size: pageSize,
    }));
  }


  const handleDelete = (productId) => {
    Swal.fire({
      title: t('product.deleteConfirmTitle'),
      text: t('product.deleteConfirmText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {

          let payload = {
            id: productId,
            onSuccess: () => {
              Swal.fire(t('product.deleted'), '', 'success');
              refrechProducts();
            },
            onError: (error) => {
              Swal.fire({
                icon: 'error',
                title: t('product.deleteError'),
                text: t('product.deleteErrorMessage')
              });
            }
          }
          dispatch(productActions.deleteProduct(payload));

        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: t('common.error'),
            text: t('product.deleteError')
          });
        }
      }
    });
  };



  const handleEdit = (product) => {
    navigate(PRODUCTS_FORM_PAGE, { state: { product } });
  };



  const downloadTemplate = () => {
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const headers = [
      [
        'Libellé',
        'Catégorie',
        'Unité',
        'Genre',
        'Couleur',
        'Prix d\'achat',
        'Prix Vente',
        'Stock Disponible'
      ]
    ];

    // Create worksheet with headers
    const ws = XLSX.utils.aoa_to_sheet(headers);

    // Set column widths
    const colWidths = [
      { wch: 30 },  // Libellé
      { wch: 20 },  // Catégorie
      { wch: 15 },  // Unité
      { wch: 15 },  // Genre
      { wch: 15 },  // Couleur
      { wch: 15 },  // Prix d'achat
      { wch: 15 },  // Prix Vente
      { wch: 15 }   // Stock Disponible
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Products Template");

    // Generate and download file
    XLSX.writeFile(wb, "products_template.xlsx");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: t('product.invalidFileType'),
          text: t('product.excelOnly'),
        });
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        let payload = {
          formData: formData,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('product.uploadSuccess'),
              confirmButtonText: 'OK',
              confirmButtonColor: '#198754'
            });
            refrechProducts();
          },
        }
        dispatch(productActions.uploadProducts(payload));

      } catch (error) {
        console.error('Error parsing Excel:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur!',
          text: "Erreur lors de l'importation des produits",
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      } finally {
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  }

  const DisplayToggle = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="btn-group">
            <Button
              color={displayMode === 'table' ? 'primary' : 'light'}
              onClick={() => setDisplayMode('table')}
              size="sm"
            >
          <FaList className="me-1" /> {t('common.table')}
            </Button>
            <Button
              color={displayMode === 'card' ? 'primary' : 'light'}
              onClick={() => setDisplayMode('card')}
              size="sm"
            >
          <FaTh className="me-1" /> {t('common.cards')}
            </Button>
          </div>

      <div className="d-flex align-items-center gap-3">
        <small className="text-muted">
          {t('product.showing', {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage: currentPage,
            totalPages: totalPages
          })}
        </small>

        <div className="d-flex align-items-center gap-2">
          <Button color="light" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
              <FaAngleDoubleLeft />
            </Button>
          <Button color="light" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <FaAngleLeft />
            </Button>

          <span className="mx-2">
            {currentPage} / {totalPages}
          </span>

          <Button color="light" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              <FaAngleRight />
            </Button>
          <Button color="light" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
              <FaAngleDoubleRight />
            </Button>
          </div>
        </div>
      </div>
    );

  const TableView = () => (
    <>
      <div className="cart-container" style={{
        height: '600px',
        overflowY: 'auto'
      }}>
        <Table hover bordered className="align-middle mb-0">
          <thead className="bg-light">
            <tr>
              <th className="align-middle">
                <FaBarcode className="me-2 text-primary" size={12} />
                {t('product.code')}
              </th>
              <th className="align-middle">
                <FaBox className="me-2 text-primary" size={12} />
                {t('product.description')}
              </th>
              <th>{t('product.genre')}</th>
              <th>{t('product.couleurs')}</th>
              <th className="align-middle text-end">
                <FaTag className="me-2 text-primary" size={12} />
                {t('product.salePrice')}
              </th>
              <th className="align-middle text-end">
                <FaTag className="me-2 text-primary" size={12} />
                {t('product.buyingPrice')}
              </th>
              <th className="align-middle text-center">
                <FaBoxes className="me-2 text-primary" size={12} />
                {t('product.stockPhysique')}
              </th>
              <th className="align-middle text-center" style={{ width: '120px' }}>
                <FaCog className="me-2 text-primary" size={12} />
                {t('common.actions')}
              </th>
            </tr>
          </thead>

          <tbody>
            {!products || products.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  <div className="text-muted">
                    {t('product.noProducts')}
                  </div>
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.id}>
                  <td className="align-middle">{product.code}</td>
                  <td className="align-middle">
                    {product.categorieLibelle} - {product.libelle.length > 30 ? product.libelle.substring(0, 30) + '...' : product.libelle}
                  </td>
                  <td>
                    {product.genre && (
                      <Badge color={product.genre === 'HOMME' ? 'primary' : 'pink'}>
                        {t(`product.${product.genre.toLowerCase()}`)}
                      </Badge>
                    )}
                  </td>
                  <td>
                    <div className="colors-display">
                      {product.couleurs?.slice(0, 3).map(couleur => {
                        const couleurOption = couleurOptions.find(c => c.value === couleur);
                        return (
                          <span
                            key={couleur}
                            className="color-dot"
                            style={{ backgroundColor: couleurOption?.color }}
                            title={couleurOption?.label}
                          ></span>
                        );
                      })}
                      {product.couleurs?.length > 3 && (
                        <span className="color-more">+{product.couleurs.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="align-middle text-end">
                    {formatPrice(product.prixVenteTTC1)}
                  </td>
                  <td className="align-middle text-end">{formatPrice(product.prixAchatTTC)}</td>
                  <td className="align-middle text-center">{product.stockPhysique} {unites.find(u => u.code === product.unite)?.libelle}</td>
                  <td className="align-middle text-center">
                    <Button color="warning" size="sm" className="me-1 px-2 py-1">
                      <FaEdit size={12} onClick={() => handleEdit(product)} />
                    </Button>
                    <Button color="danger" size="sm" className="px-2 py-1">
                      <FaTrash size={12} onClick={() => handleDelete(product.id)} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          {t('product.showing', {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage: currentPage,
            totalPages: totalPages
          })}
        </small>
      </div>
    </>
  );




  const CardView = () => {
    return (
      <div className="cart-container" style={{ height: '600px', overflowY: 'auto', padding: '0.25rem' }}>
        <Row xs={2} md={3} lg={4} xl={5} className="g-2">
          {(!products || products.length === 0) && (
            <Col xs={12} className="text-center py-4">
              <div className="text-muted">
                <FaImage size={32} className="mb-2 opacity-50" />
                <p className="mb-0 small">{t('product.noProducts')}</p>
              </div>
            </Col>
          )}
          {
            products.map(product => (
              <Col key={product.id}>
                <Card
                  className="product-card-compact h-100 border-0 shadow-sm position-relative overflow-hidden"
                  style={{
                    transition: 'all 0.2s ease',
                    borderRadius: '8px',
                    minHeight: '240px' // Slightly increased to accommodate colors
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.12)';
                    e.currentTarget.style.borderColor = '#0d6efd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.05)';
                  }}
                >
                  {/* Action buttons overlay - only visible on hover */}
                  <div
                    className="position-absolute d-flex align-items-center justify-content-center gap-1 opacity-0 product-actions-overlay"
                    style={{
                      top: '0',
                      left: '0',
                      right: '0',
                      height: '100px',
                      background: 'rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(1px)',
                      transition: 'opacity 0.2s ease',
                      borderRadius: '8px 8px 0 0',
                      zIndex: 5
                    }}
                  >
                    <Button
                      color="warning"
                      size="sm"
                      className="rounded-circle p-1 shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(product);
                      }}
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FaEdit size={15} />
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      className="rounded-circle p-1 shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(product.id);
                      }}
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FaTrash size={15} />
                    </Button>
                  </div>

                  {/* Compact image container */}
                  <div
                    className="position-relative overflow-hidden"
                    style={{
                      height: '150px',
                      borderRadius: '8px 8px 0 0'
                    }}
                  >
                    {product.photo ? (
                      <img
                        src={`${config.baseURL}${product.photo}`}
                        alt={product.libelle}
                        className="w-100 h-100"
                        style={{
                          objectFit: 'cover',
                          transition: 'transform 0.2s ease'
                        }}
                      />
                    ) : (
                      <div
                        className="w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
                        }}
                      >
                        <FaImage size={24} className="text-muted opacity-50" />
                      </div>
                    )}

                    {/* Stock indicator badge */}
                    {product.stockPhysique !== undefined && (
                      <Badge
                        color={product.stockPhysique > 10 ? 'success' : product.stockPhysique > 0 ? 'warning' : 'danger'}
                        className="position-absolute top-0 end-0 m-1"
                        style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem', zIndex: 6 }}
                      >
                        {product.stockPhysique > 0 ? product.stockPhysique : '0'}
                      </Badge>
                    )}
                  </div>

                  <CardBody className="p-2 d-flex flex-column">
                    {/* Product code */}
                    {product.code && (
                      <div className="mb-1">
                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                          {product.code}
                        </small>
                      </div>
                    )}

                    {/* Compact product title */}
                    <div
                      className="product-title-compact mb-1 fw-semibold text-dark lh-sm"
                      style={{
                        fontSize: '1rem',
                        minHeight: '2.25rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                      title={product.libelle}
                    >
                      {product.categorieLibelle} - {product.libelle}
                    </div>

                    {/* Genre and Colors section */}
                    <div className="mb-2">
                      {/* Genre badge */}
                      {product.genre && (
                        <div className="mb-1">
                          <Badge
                            color={product.genre === 'HOMME' ? 'primary' : 'pink'}
                            style={{ fontSize: '0.6rem' }}
                          >
                            {t(`product.${product.genre.toLowerCase()}`)}
                          </Badge>
                        </div>
                      )}

                      {/* Colors display */}
                      {product.couleurs && product.couleurs.length > 0 && (
                        <div className="colors-display-compact">
                          <div className="d-flex flex-wrap align-items-center gap-1">
                            {product.couleurs.slice(0, 6).map((couleur, index) => {
                              const couleurOption = couleurOptions.find(c => c.value === couleur);
                              return (
                                <span
                                  key={`${product.id}-${couleur}-${index}`}
                                  className="color-dot-compact"
                                  style={{
                                    backgroundColor: couleurOption?.color || '#6c757d',
                                    border: couleurOption?.color === '#FFFFFF' ? '1px solid #dee2e6' : '1px solid rgba(0,0,0,0.1)'
                                  }}
                                  title={couleurOption?.label || couleur}
                                ></span>
                              );
                            })}
                            {product.couleurs.length > 6 && (
                              <span className="color-more-compact">
                                +{product.couleurs.length - 6}
                              </span>
                            )}
                          </div>
                          {product.couleurs.length > 0 && (
                            <small className="text-muted d-block mt-1" style={{ fontSize: '0.6rem' }}>
                              {product.couleurs.length} couleur{product.couleurs.length > 1 ? 's' : ''}
                            </small>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Price and stock section */}
                    <div className="mt-auto">
                      <div className="mb-1">
                        <div
                          className="fw-bold text-success"
                          style={{ fontSize: '0.8rem', lineHeight: '1' }}
                        >
                          {formatPrice(product.prixVenteTTC1)} / {unites.find(u => u.code === product.unite)?.libelle || product.unite}
                        </div>

                      </div>

                      {/* Purchase price */}
                      {product.prixAchatTTC && (
                        <div className="mb-1">
                          <small
                            className="text-muted"
                            style={{ fontSize: '0.6rem' }}
                          >
                            Achat: {formatPrice(product.prixAchatTTC)}
                          </small>
                        </div>
                      )}

                      {/* Stock info */}
                      <div className="d-flex align-items-center justify-content-between">
                        <small
                          className="text-muted"
                          style={{ fontSize: '0.6rem' }}
                        >
                          Stock: {product.stockPhysique} {unites.find(u => u.code === product.unite)?.libelle || product.unite}
                        </small>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))
          }
        </Row>

        {/* Custom styles */}
        <style jsx>{`
        .product-card-compact:hover .product-actions-overlay {
          opacity: 1 !important;
        }
        
        .product-card-compact {
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.05);
        }
        
        .product-title-compact {
          word-break: break-word;
          color: #2c3e50;
        }
        
        .colors-display-compact {
          min-height: 20px;
        }
        
        .color-dot-compact {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          display: inline-block;
          cursor: pointer;
          transition: transform 0.1s ease;
        }
        
        .color-dot-compact:hover {
          transform: scale(1.2);
        }
        
        .color-more-compact {
          font-size: 0.6rem;
          color: #6c757d;
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .product-card-compact {
            min-height: 220px;
          }
          
          .product-actions-overlay {
            height: 80px !important;
          }
          
          .color-dot-compact {
            width: 12px;
            height: 12px;
          }
        }
        
        @media (max-width: 576px) {
          .product-card-compact {
            min-height: 200px;
          }
          
          .product-actions-overlay {
            height: 70px !important;
          }
          
          .color-dot-compact {
            width: 10px;
            height: 10px;
          }
        }
      `}</style>
      </div>
    );
  };




  return (
    <Container fluid>
      <Row>
        <Col md={12} className="pe-4">
          <Card className="shadow-sm h-100">
            <CardBody>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                  <div className="mt-2 text-muted">
                    {t('common.loadingData')}
                  </div>
                </div>
              ) : (<>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  {/* Search Input - Left Side */}
                  <div className="position-relative flex-grow-1 me-3">
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      placeholder={t('product.searchPlaceholder')}
                      value={searchTerm.toUpperCase()}
                      onKeyDown={handleSearchKeyDown}
                      onChange={handleSearchChange}
                      className={`ps-4 ${isSearching ? 'searching' : ''}`}
                    />
                    <FaSearch
                      style={{
                        position: 'absolute',
                        left: '10px',
                        top: '12px',
                        color: '#6c757d'
                      }}
                    />
                  </div>

                  {/* Buttons - Right Side */}
                  <div className="d-flex mx-3">
                    <Button color="primary" size="sm"
                      className="me-2" onClick={() => navigate(PRODUCTS_FORM_PAGE)}>
                      <FaPlus className="me-2" />
                      {t('product.new')}
                    </Button>
                    {/* <Button
                      color="secondary"
                      size="sm"
                      className="me-2"
                      onClick={downloadTemplate}
                    >
                      <FaFileDownload className="me-2" size={12} />
                      {t('product.downloadTemplate')}
                    </Button> */}
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <FaFileUpload className="me-2" size={12} />
                      {t('product.uploadProducts')}
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".xlsx,.xls"
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
                <DisplayToggle />
                {displayMode === 'table' ? <TableView /> : <CardView />}
              </>)}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProductList;
